/**
 * AppICEMAP — CommentInput Component
 * Inline input for posting comments with character count.
 *
 * Tag: <comment-input>
 *
 * Properties:
 *   .placeholder — placeholder text
 *   .value       — current input value
 *   .disabled    — boolean
 *   .parentId    — reply target comment ID (null for top-level)
 *   .replyToAuthor — name of author being replied to
 *
 * Dispatches:
 *   comment-input:submit → { body: string, parentId: string | null }
 *   comment-input:cancel-reply → { }
 */

export class CommentInput extends HTMLElement {
	constructor() {
		super();
		this._placeholder = "Añadir comentario...";
		this._value = "";
		this._disabled = false;
		this._parentId = null;
		this._replyToAuthor = null;
		this._maxLength = 500;
	}

	connectedCallback() {
		this._render();
		this._bindEvents();
	}

	/* ── Public API ────────────────────────────────────────────────────────── */

	get value() {
		return this._input ? this._input.value : this._value;
	}
	set value(val) {
		this._value = val || "";
		if (this._input) this._input.value = this._value;
		this._updateCounter();
		this._updateSendButton();
	}

	get disabled() {
		return this._disabled;
	}
	set disabled(val) {
		this._disabled = !!val;
		if (this._input) this._input.disabled = this._disabled;
		this._updateSendButton();
	}

	get parentId() {
		return this._parentId;
	}
	set parentId(val) {
		this._parentId = val || null;
		this._updateReplyIndicator();
	}

	get replyToAuthor() {
		return this._replyToAuthor;
	}
	set replyToAuthor(val) {
		this._replyToAuthor = val || null;
		this._updateReplyIndicator();
	}

	focus() {
		if (this._input) this._input.focus();
	}

	clearReply() {
		this._parentId = null;
		this._replyToAuthor = null;
		this._placeholder = "Añadir comentario...";
		this._updateReplyIndicator();
		if (this._input) this._input.placeholder = this._placeholder;
	}

	/* ── Render ────────────────────────────────────────────────────────────── */

	_render() {
		this.innerHTML = `
      <div class="comment-input__reply-indicator" hidden></div>
      <div class="comment-input">
        <div class="comment-input__avatar" aria-hidden="true">?</div>
        <div class="comment-input__field">
          <input type="text" maxlength="${this._maxLength}"
                 placeholder="${escapeAttr(this._placeholder)}"
                 value="${escapeAttr(this._value)}"
                 aria-label="Escribir comentario">
          <span class="comment-input__counter">${this._value.length}/${this._maxLength}</span>
        </div>
        <button class="comment-input__send" aria-label="Enviar comentario" disabled>
          <i class="ph ph-paper-plane-right" aria-hidden="true"></i>
        </button>
      </div>
    `;

		this._input = this.querySelector("input");
		this._sendBtn = this.querySelector(".comment-input__send");
		this._counterEl = this.querySelector(".comment-input__counter");
		this._replyIndicator = this.querySelector(
			".comment-input__reply-indicator",
		);

		// Set user initials from store
		this._updateAvatar();
	}

	_updateAvatar() {
		const avatar = this.querySelector(".comment-input__avatar");
		if (!avatar) return;

		try {
			// Try to get user from localStorage session
			const session = JSON.parse(
				localStorage.getItem("appicemap-session") || "{}",
			);
			// Not always available — keep "?" as fallback
		} catch {
			/* ignore */
		}
	}

	/* ── Events ────────────────────────────────────────────────────────────── */

	_bindEvents() {
		if (!this._input) return;

		this._input.addEventListener("input", () => {
			this._value = this._input.value;
			this._updateCounter();
			this._updateSendButton();
		});

		this._input.addEventListener("keydown", (e) => {
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				this._submit();
			}
		});

		if (this._sendBtn) {
			this._sendBtn.addEventListener("click", () => this._submit());
		}
	}

	_submit() {
		const body = (this._value || "").trim();
		if (!body || this._disabled) return;

		this.dispatchEvent(
			new CustomEvent("comment-input:submit", {
				detail: {
					body,
					parentId: this._parentId,
				},
				bubbles: true,
			}),
		);

		// Clear input
		this._value = "";
		if (this._input) this._input.value = "";
		this._updateCounter();
		this._updateSendButton();

		// Clear reply mode
		this.clearReply();
		this._updateReplyIndicator();
	}

	_updateCounter() {
		if (!this._counterEl) return;
		const len = this._value.length;
		this._counterEl.textContent = `${len}/${this._maxLength}`;
		this._counterEl.classList.toggle("comment-input__counter--over", len > 400);
	}

	_updateSendButton() {
		if (!this._sendBtn) return;
		this._sendBtn.disabled = !this._value.trim() || this._disabled;
	}

	_updateReplyIndicator() {
		if (!this._replyIndicator) return;

		if (this._parentId && this._replyToAuthor) {
			this._replyIndicator.hidden = false;
			this._replyIndicator.innerHTML = `
        <span>Respondiendo a <strong>${escapeHtml(this._replyToAuthor)}</strong></span>
        <button class="cancel-reply-btn" aria-label="Cancelar respuesta">Cancelar</button>
      `;

			this._replyIndicator
				.querySelector(".cancel-reply-btn")
				?.addEventListener("click", () => {
					this.clearReply();
					this.dispatchEvent(
						new CustomEvent("comment-input:cancel-reply", { bubbles: true }),
					);
				});

			if (this._input) {
				this._input.placeholder = `Responder a ${this._replyToAuthor}...`;
			}
		} else {
			this._replyIndicator.hidden = true;
			this._replyIndicator.innerHTML = "";
			if (this._input) {
				this._input.placeholder = this._placeholder;
			}
		}
	}
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str;
	return div.innerHTML;
}

function escapeAttr(str) {
	return (str || "").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

customElements.define("comment-input", CommentInput);
